/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import useContentTest from './hooks'
import './style.scss'
import { MessageTypes, Role } from '../../lib/content-test/shared/Types'
import NavBar from './components/NavBar/NavBar'
import ContentContainer from './components/ContentContainer/ContentContainer'
import Form from './components/Form/Form'
import Interface from '../../lib/content-test/shared/Interface'
import { ModalProvider } from './contexts/ModalContext'
import ContentTestLoading from './components/Loading/Loading'
import { useAppBridge } from '@shopify/app-bridge-react'
import { Fullscreen } from '@shopify/app-bridge/actions'
import useCustomHistory from '../../hooks/useCustomHistory'
import ChangeModeModal from './components/NavBar/components/ChangeModeModal'
import Modes from '../../lib/content-test/shared/Modes'
import { Banner, Button } from '@shopify/polaris'

function ContentTestEditor({ shop, setShowToast, setMessage }) {
  const params = useParams()
  const containerRef = useRef()
  const app = useAppBridge()
  const history = useCustomHistory()
  const fullscreen = Fullscreen.create(app)
  const [selectedMode, setSelectedMode] = useState(undefined)
  const [previewError, setPreviewError] = useState('')
  const [previewReady, setPreviewReady] = useState(false)
  const [retryKey, setRetryKey] = useState(0)
  const [saving, setSaving] = useState(false)
  const savingRef = useRef(false)
  const [urlPath, setUrlPath] = useState('/')
  const fullUrl = (path = urlPath) => shop && `https://${shop.domain}${path}`
  const [currentURL, setCurrentURL] = useState(fullUrl())
  const [selectedElement, setSelectedElement] = useState(undefined)
  const [openModal, setOpenModal] = useState(false)
  const [path, setPath] = useState('')
  const [deviceType, setDeviceType] = useState('desktop')

  const changeElementInThePage = (newSelectedElement, keepElement) => {
    if (selectedElement && !newSelectedElement && !keepElement) {
      sendElementChangeToThePage({ ...selectedElement, newStyle: selectedElement.initialStyle })
    }
    setSelectedElement(newSelectedElement)
  }

  const handleInteract = (data) => {
    setPath(data.path)

    if (selectedMode === Modes.Interact) {
      setOpenModal(true)
    } else {
      setOpenModal(false)
    }
  }

  const sendModeChangeToThePage = () => sendMessage({ messageType: MessageTypes.Editor.ModeChanged, data: { selectedMode } })
  const loadTestIntoPage = () => sendMessage({ messageType: MessageTypes.Editor.EditorOpened, data: { contentTest, urlPath: new URL(urlPath, 'https://preview.invalid').pathname } })
  const sendElementChangeToThePage = (selectedElement) => selectedElement && sendMessage({ messageType: MessageTypes.Editor.ElementChanged, data: { elementChange: selectedElement } })

  useEffect(sendModeChangeToThePage, [selectedMode])
  useEffect(loadTestIntoPage, [urlPath])
  useEffect(() => sendElementChangeToThePage(selectedElement), [selectedElement])

  const { contentTest, save, dispatch, loadError, reload } = useContentTest(params.id)

  const sendMessage = ({ messageType, data }) => {
    if (!containerRef.current?.contentWindow) return
    Interface.build(Role.Editor, containerRef.current.contentWindow).send({ messageType, data })
  }

  useEffect(() => {
    if (!contentTest._id || !containerRef.current) return
    const interface_ = Interface.build(Role.Editor, containerRef.current.contentWindow)
    return interface_.subscribeToMessages({ messageMapping: {
      [MessageTypes.Page.ElementSelected]: ({ data }) => {
        const change = data.elementChange
        if (change && typeof change.selector?.path === 'string' && typeof change.initialStyle?.text === 'string' && typeof change.newStyle?.text === 'string') setSelectedElement(change)
      },
      [MessageTypes.Page.UrlPathChanged]: ({ data }) => {
        if (typeof data.path === 'string' && data.path.startsWith('/') && !data.path.startsWith('//')) handleInteract(data)
      }
    } })
  }, [contentTest._id, selectedMode, retryKey, urlPath])

  useEffect(() => {
    if (!contentTest._id) return
    setPreviewReady(false)
    setPreviewError('')
    setSelectedMode(undefined)
    setSelectedElement(undefined)
    const timer = setTimeout(() => setPreviewError('The storefront did not finish loading. Please retry. If this continues, contact support.'), 20000)
    let ready = false
    const receive = event => {
      if (event.source !== containerRef.current?.contentWindow || event.data?.source !== 'trident-preview') return
      if (event.data.type === 'error') {
        clearTimeout(timer)
        setPreviewReady(false)
        setPreviewError('Unable to load your storefront. Please retry or contact support.')
      }
      if (event.data.type === 'loaded') {
        if (ready) return
        ready = true
        clearTimeout(timer)
        setPreviewReady(true)
        setPreviewError('')
        loadTestIntoPage()
        setSelectedMode('edit')
      }
    }
    window.addEventListener('message', receive)
    return () => { clearTimeout(timer); window.removeEventListener('message', receive) }
  }, [contentTest._id, urlPath, retryKey])

  const onSave = async () => {
    if (!previewReady || savingRef.current) return
    savingRef.current = true
    setSaving(true)
    try {
      await save()
      fullscreen.dispatch(Fullscreen.Action.EXIT)
      setShowToast(true)
      setMessage('Saved')
      history.push('/')
    } catch (error) {
      setPreviewError('Your changes could not be saved. Please try Save again. Your edits are still here.')
    } finally { savingRef.current = false; setSaving(false) }
  }

  if (loadError) return <Banner status="critical" title="Unable to load this content test"><Button onClick={reload}>Retry</Button></Banner>
  if (!contentTest._id) return <>Loading...</>

  return (
    <div className="ContentTestEditor">
      <NavBar
        contentTest={contentTest}
        onSave={onSave}
        saveDisabled={!previewReady || saving}
        selectedMode={selectedMode}
        setSelectedMode={setSelectedMode}
        urlPath={urlPath}
        setUrlPath={setUrlPath}
        setOpenModal={setOpenModal}
        deviceType={deviceType}
        setDeviceType={setDeviceType}
        setSelectedElement={setSelectedElement}
        sendElementChangeToThePage={sendElementChangeToThePage}
        shop={shop}
        currentURL={currentURL}
        setCurrentURL={setCurrentURL}
        fullUrl={fullUrl}
      />
      {previewError && <Banner status="critical" title={previewError}><Button onClick={() => setRetryKey(value => value + 1)}>Retry storefront</Button></Banner>}
      {!previewError && <ContentTestLoading selectedMode={selectedMode} />}
      <ContentContainer pageRef={containerRef} contentTest={contentTest} urlPath={urlPath} setUrlPath={setUrlPath} retryKey={retryKey} deviceType={deviceType}/>
      <ModalProvider>
        <Form
          selectedElement={selectedElement}
          setSelectedElement={setSelectedElement}
          contentTest={contentTest}
          changeElementInThePage={changeElementInThePage}
          urlPath={new URL(urlPath, 'https://preview.invalid').pathname}
          dispatch={dispatch}
        />
      </ModalProvider>
      <ChangeModeModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        selectedMode={selectedMode}
        setSelectedMode={setSelectedMode}
        setUrlPath={setUrlPath}
        currentURL={currentURL}
        setCurrentURL={setCurrentURL}
        fullUrl={fullUrl}
        path={path}
        setPath={setPath}
        dispatch={dispatch}
        contentTest={contentTest}
      />
    </div>
  )
}

export default ContentTestEditor
